import { NavLink } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { appsForRole, type App } from '@/apps/registry';
import { useAuthStore } from '@/store/authStore';
import clsx from 'clsx';

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const apps = user ? appsForRole(user.role) : [];

  return (
    <aside className="w-64 bg-surf-bg-1 border-r border-surf-border flex flex-col">
      <div className="px-5 py-6 border-b border-surf-border">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-surf-accent flex items-center justify-center">
            <Icons.Activity size={16} className="text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">
              Surf Monitor
            </div>
            <div className="text-2xs text-surf-text-tertiary font-mono">
              v0.1.0 · F0
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <SectionLabel>Monitoramento</SectionLabel>
        {apps.map((app) => (
          <AppLink key={app.id} app={app} />
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-surf-border space-y-2">
        <div className="text-2xs text-surf-text-tertiary font-mono">
          {user?.sub}
        </div>
        <div className="text-2xs text-surf-text-tertiary font-mono uppercase">
          {user?.role}
        </div>
        <button
          onClick={() => useAuthStore.getState().logout()}
          className="text-2xs text-surf-text-secondary hover:text-surf-accent-red transition-colors font-mono"
        >
          → sair
        </button>
      </div>
    </aside>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-2xs uppercase tracking-wider text-surf-text-tertiary font-mono">
      {children}
    </div>
  );
}

function AppLink({ app }: { app: App }) {
  // Lookup do icon (lucide-react)
  const IconComp = (Icons as unknown as Record<string, React.ComponentType<{ size?: number }>>)[
    app.icon
  ] || Icons.Box;

  return (
    <NavLink
      to={`/app/${app.id}`}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors group',
          isActive
            ? 'bg-surf-bg-2 text-surf-text-primary'
            : 'text-surf-text-secondary hover:bg-surf-bg-2/50 hover:text-surf-text-primary',
        )
      }
    >
      <IconComp size={16} />
      <span className="flex-1">{app.label}</span>
      <StatusBadge status={app.status} />
    </NavLink>
  );
}

function StatusBadge({ status }: { status: App['status'] }) {
  const cfg = {
    live: { label: '★', cls: 'text-surf-accent-green' },
    wip: { label: '◐', cls: 'text-surf-accent-yellow' },
    legacy: { label: '·', cls: 'text-surf-text-tertiary' },
  }[status];

  return <span className={clsx('text-2xs font-mono', cfg.cls)}>{cfg.label}</span>;
}
